/**
 * v132 — AdvancedPlotControls.
 *
 * Precise numeric entry alternative to the Quick tap-to-place flow.
 * Paired number inputs (X, Y) + horizontal sliders + unit toggle (in / cm).
 *
 * Canonical storage stays in inches; displays respect the user's unit pref.
 * Mobile-first: 44px+ touch targets, no overlapping inputs, both portrait
 * and landscape friendly.
 *
 * Props:
 *   pending     — current pending offset in inches (canonical)
 *   onChange    — fired with the new (xIn, yIn) on every adjustment
 *   onLock      — fires when user commits (the parent then calls onPlace)
 *   halfExtentIn — slider bound; matches the target's half extent
 *   unit        — current display unit
 *   onUnitToggle — toggles in <-> cm
 */
import { useEffect, useState } from 'react';
import type { DisplayUnit } from '../v3-schema';
import { inToCm, cmToIn, formatOffsetDisplay } from '../v3-schema';

interface AdvancedPlotControlsProps {
  pending: { x: number; y: number };
  onChange: (xIn: number, yIn: number) => void;
  halfExtentIn?: number;
  unit: DisplayUnit;
  onUnitToggle: (unit: DisplayUnit) => void;
}

export function AdvancedPlotControls({
  pending, onChange, halfExtentIn = 6, unit, onUnitToggle,
}: AdvancedPlotControlsProps) {
  const stepIn = unit === 'cm' ? cmToIn(0.1) : 0.1; // 1 mm or 0.1"
  const halfExtent = unit === 'cm' ? inToCm(halfExtentIn) : halfExtentIn;
  const fmt = formatOffsetDisplay(pending.x, pending.y, unit);

  // Local input state mirrors pending (numbered string form) so typing "-" works.
  const [xText, setXText] = useState<string>(() => displayValue(pending.x, unit));
  const [yText, setYText] = useState<string>(() => displayValue(pending.y, unit));

  useEffect(() => { setXText(displayValue(pending.x, unit)); }, [pending.x, unit]);
  useEffect(() => { setYText(displayValue(pending.y, unit)); }, [pending.y, unit]);

  const setX = (rawText: string) => {
    setXText(rawText);
    const n = parseFloat(rawText);
    if (!Number.isFinite(n)) return;
    const xIn = unit === 'cm' ? cmToIn(n) : n;
    onChange(round2(xIn), pending.y);
  };
  const setY = (rawText: string) => {
    setYText(rawText);
    const n = parseFloat(rawText);
    if (!Number.isFinite(n)) return;
    const yIn = unit === 'cm' ? cmToIn(n) : n;
    onChange(pending.x, round2(yIn));
  };

  const xDisplay = unit === 'cm' ? inToCm(pending.x) : pending.x;
  const yDisplay = unit === 'cm' ? inToCm(pending.y) : pending.y;

  return (
    <div className="space-y-3">
      {/* Live readout above the target */}
      <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-md px-3 py-2">
        <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Offset</span>
        <span className="text-sm font-mono text-cyan-300 tabular-nums">{fmt.full}</span>
        <button
          onClick={() => onUnitToggle(unit === 'in' ? 'cm' : 'in')}
          className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded border border-zinc-700 text-zinc-300 hover:border-cyan-500/60 hover:text-cyan-300 touch-manipulation"
          title="Toggle in / cm"
        >
          {unit === 'in' ? 'in' : 'cm'}
        </button>
      </div>

      {/* Numeric inputs */}
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">
            X (R+ / L−)
          </span>
          <input
            type="number"
            inputMode="decimal"
            value={xText}
            onChange={(e) => setX(e.target.value)}
            step={unit === 'cm' ? 0.1 : 0.1}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm font-mono tabular-nums"
            placeholder="0"
            aria-label={`Horizontal offset in ${unit === 'cm' ? 'centimeters' : 'inches'}`}
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">
            Y (DN+ / UP−)
          </span>
          <input
            type="number"
            inputMode="decimal"
            value={yText}
            onChange={(e) => setY(e.target.value)}
            step={unit === 'cm' ? 0.1 : 0.1}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm font-mono tabular-nums"
            placeholder="0"
            aria-label={`Vertical offset in ${unit === 'cm' ? 'centimeters' : 'inches'}`}
          />
        </label>
      </div>

      {/* Sliders — thumb-scrub for fine adjust */}
      <div className="space-y-2 pt-1">
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center justify-between mb-1">
            <span>X slider</span>
            <span className="text-cyan-400 tabular-nums">{fmt.x}</span>
          </span>
          <input
            type="range"
            min={-halfExtent}
            max={halfExtent}
            step={stepIn}
            value={xDisplay}
            onChange={(e) => {
              const n = parseFloat(e.target.value);
              const xIn = unit === 'cm' ? cmToIn(n) : n;
              onChange(round2(xIn), pending.y);
            }}
            className="w-full sx-range touch-manipulation"
            style={{ minHeight: 44 }}
            aria-label="X offset slider"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center justify-between mb-1">
            <span>Y slider</span>
            <span className="text-cyan-400 tabular-nums">{fmt.y}</span>
          </span>
          <input
            type="range"
            min={-halfExtent}
            max={halfExtent}
            step={stepIn}
            value={yDisplay}
            onChange={(e) => {
              const n = parseFloat(e.target.value);
              const yIn = unit === 'cm' ? cmToIn(n) : n;
              onChange(pending.x, round2(yIn));
            }}
            className="w-full sx-range touch-manipulation"
            style={{ minHeight: 44 }}
            aria-label="Y offset slider"
          />
        </label>
      </div>

      {/* Reset to center */}
      <button
        onClick={() => onChange(0, 0)}
        className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-300 mx-auto block py-1"
      >
        Center
      </button>
    </div>
  );
}

function round2(n: number): number { return Math.round(n * 100) / 100; }
function displayValue(xIn: number, unit: DisplayUnit): string {
  const v = unit === 'cm' ? inToCm(xIn) : xIn;
  return v === 0 ? '0' : v.toFixed(unit === 'cm' ? 1 : 2);
}
